// src/contexts/DirectoryContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import api from "../context/api";
import { useAuth } from "../context/AuthContext";

const DirectoryContext = createContext(null);

// simple module-level cache (survives route changes; resets on full refresh)
let _cache = null;
let _cachePromise = null;

async function fetchDirectoryOnce() {
  if (_cache) return _cache;
  if (_cachePromise) return _cachePromise;

  _cachePromise = api
    .get("/api/other/symbol_list")
    .then((res) => {
      const map = {};

      if (Array.isArray(res.data)) {
        res.data.forEach((row) => {
          if (row.symbol && row.name) {
            map[row.symbol] = {
              name: row.name,
              quoteType: row.quoteType || "",
            };
          }
        });
      }

      _cache = map;
      return _cache;
    })
    .finally(() => {
      _cachePromise = null;
    });

  return _cachePromise;
}

export function DirectoryProvider({ children }) {
  const { user, initializing } = useAuth();

  const [directory, setDirectory] = useState(_cache || {});
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  const refresh = async () => {
    _cache = null;
    _cachePromise = null;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchDirectoryOnce();
      setDirectory(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initializing) return;

    let alive = true;

    // Clear cache when auth state changes
    _cache = null;
    _cachePromise = null;

    setLoading(true);

    fetchDirectoryOnce()
      .then((data) => alive && setDirectory(data))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [initializing, user?.uid]);

  const value = useMemo(
    () => ({
      directory,
      loading,
      error,
      refresh
    }),
    [directory, loading, error]
  );

  return (
    <DirectoryContext.Provider value={value}>
      {children}
    </DirectoryContext.Provider>
  );
}

export function useDirectory() {
  const ctx = useContext(DirectoryContext);

  if (!ctx) {
    throw new Error(
      "useDirectory() must be used inside <DirectoryProvider>"
    );
  }

  return ctx;
}